// src/components/CommonForBoth/TopBanner/TopNotificationBanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { get, post } from 'helpers/api_helper';
import cookieHelper from 'helpers/getCookieData';
import {
  RiCloseLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiExternalLinkLine,
  RiMapPin2Line,
  RiMegaphoneLine,
  RiMagicLine,
  RiTimeLine,
} from 'react-icons/ri';

const BANNER_THEMES = {
  primary: { bg: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', text: '#ffffff', border: '#60a5fa', btnBg: '#ffffff', btnColor: '#1e3a8a' },
  'gradient-purple': { bg: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #9333ea 100%)', text: '#ffffff', border: '#c084fc', btnBg: '#ffffff', btnColor: '#6b21a8' },
  'gradient-amber': { bg: 'linear-gradient(135deg, #78350f 0%, #d97706 50%, #f59e0b 100%)', text: '#ffffff', border: '#fcd34d', btnBg: '#ffffff', btnColor: '#92400e' },
  'gradient-emerald': { bg: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #10b981 100%)', text: '#ffffff', border: '#6ee7b7', btnBg: '#ffffff', btnColor: '#065f46' },
  'gradient-rose': { bg: 'linear-gradient(135deg, #881337 0%, #e11d48 50%, #f43f5e 100%)', text: '#ffffff', border: '#fda4af', btnBg: '#ffffff', btnColor: '#9f1239' },
  danger: { bg: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)', text: '#ffffff', border: '#fca5a5', btnBg: '#ffffff', btnColor: '#991b1b' },
  info: { bg: 'linear-gradient(135deg, #0f172a 0%, #0284c7 100%)', text: '#ffffff', border: '#7dd3fc', btnBg: '#ffffff', btnColor: '#0369a1' },
  warning: { bg: 'linear-gradient(135deg, #18181b 0%, #27272a 100%)', text: '#facc15', border: '#fde047', btnBg: '#facc15', btnColor: '#000000' },
};

const TopNotificationBanner = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('oralstop_dismissed_banners');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [isPaused, setIsPaused] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const timerRef = useRef(null);

  // Hidden on public/auth routes
  const hiddenPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/clinic/', '/book/', '/dr/', '/doctor/', '/lock-screen'];
  const isHiddenRoute = hiddenPaths.some((p) => location.pathname.startsWith(p));

  const fetchActiveBanners = async () => {
    try {
      const res = await get('/banners/active');
      if (res?.success && Array.isArray(res.data)) {
        setBanners(res.data);
      }
    } catch (err) {
      // Silently handle if banners endpoint is unreachable
    }
  };

  useEffect(() => {
    if (!isHiddenRoute) {
      fetchActiveBanners();
    }
  }, [location.pathname]);

  // Filter out dismissed banners
  const visibleBanners = banners.filter((b) => !dismissedIds.includes(b.id));

  // Auto carousel rotation
  useEffect(() => {
    if (visibleBanners.length <= 1 || isPaused) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
    }, 9000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visibleBanners.length, isPaused]);

  // Adjust index if out of bounds
  useEffect(() => {
    if (currentIndex >= visibleBanners.length && visibleBanners.length > 0) {
      setCurrentIndex(0);
    }
  }, [visibleBanners.length, currentIndex]);

  if (isHiddenRoute || visibleBanners.length === 0) {
    return null;
  }

  const currentBanner = visibleBanners[currentIndex] || visibleBanners[0];
  if (!currentBanner) return null;

  const theme = BANNER_THEMES[currentBanner.banner_style] || BANNER_THEMES.primary;

  const handleDismiss = (id) => {
    const updated = [...dismissedIds, id];
    setDismissedIds(updated);
    try {
      localStorage.setItem('oralstop_dismissed_banners', JSON.stringify(updated));
    } catch (e) {}
  };

  const handleActionClick = (banner) => {
    // Fire click analytics
    try {
      post(`/banners/${banner.id}/click`, {}).catch(() => {});
    } catch (e) {}

    if (banner.action_url) {
      if (banner.action_url.startsWith('http://') || banner.action_url.startsWith('https://')) {
        if (banner.open_in_new_tab) {
          window.open(banner.action_url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = banner.action_url;
        }
      } else {
        history.push(banner.action_url);
      }
    }
  };

  const nextBanner = () => {
    setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
  };

  const prevBanner = () => {
    setCurrentIndex((prev) => (prev - 1 + visibleBanners.length) % visibleBanners.length);
  };

  return (
    <div
      className="top-notification-banner-ribbon position-relative overflow-hidden"
      style={{
        background: theme.bg,
        color: theme.text,
        borderBottom: `1px solid ${theme.border}`,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
        zIndex: 1002,
        transition: 'all 0.4s ease',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container-fluid py-1.5 px-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          {/* Left / Center Content */}
          <div className="d-flex align-items-center gap-2 flex-grow-1 flex-nowrap overflow-hidden">
            {/* Multi-banner Pager Arrows (if > 1) */}
            {visibleBanners.length > 1 && (
              <div className="d-flex align-items-center gap-0.5 me-1">
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-white opacity-75 hover-opacity-100"
                  onClick={prevBanner}
                  title="Previous announcement"
                >
                  <RiArrowLeftSLine className="font-size-18" />
                </button>
                <span className="font-size-10 font-weight-bold opacity-90 px-1 py-0.5 rounded" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                  {currentIndex + 1}/{visibleBanners.length}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-white opacity-75 hover-opacity-100"
                  onClick={nextBanner}
                  title="Next announcement"
                >
                  <RiArrowRightSLine className="font-size-18" />
                </button>
              </div>
            )}

            {/* Badge */}
            <span
              className="badge font-size-10 px-2 py-0.5 rounded-pill font-weight-bold shadow-2xs d-none d-sm-inline-flex align-items-center gap-1 text-uppercase"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.22)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                letterSpacing: '0.4px',
              }}
            >
              {currentBanner.priority === 'critical' ? (
                <span>🔴 URGENT</span>
              ) : currentBanner.type === 'event' ? (
                <RiMagicLine />
              ) : (
                <RiMegaphoneLine />
              )}
              <span>{currentBanner.badge_text || currentBanner.type}</span>
            </span>

            {/* Targeting Indicator (if Geo) */}
            {currentBanner.target_scope === 'geo' && currentBanner.target_state && currentBanner.target_state.toLowerCase() !== 'all' && (
              <span
                className="badge font-size-10 px-1.5 py-0.5 rounded-pill d-none d-md-inline-flex align-items-center gap-0.5"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)', color: '#ffffff' }}
              >
                <RiMapPin2Line className="font-size-11" />
                <span>{currentBanner.target_state}</span>
              </span>
            )}

            {/* Title & Body */}
            <div className="d-flex align-items-center gap-1.5 flex-nowrap overflow-hidden text-truncate font-size-12">
              <strong className="font-weight-bold text-nowrap text-white">
                {currentBanner.title}
              </strong>
              <span className="opacity-75 d-none d-md-inline">—</span>
              <span className="opacity-90 text-truncate d-none d-md-inline font-size-12">
                {currentBanner.message}
              </span>
            </div>
          </div>

          {/* Right Action & Dismiss */}
          <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
            {currentBanner.action_text && (
              <button
                type="button"
                className="btn btn-sm font-size-11 px-2.5 py-0.5 font-weight-bold rounded-pill shadow-xs d-inline-flex align-items-center gap-1 transition-all"
                style={{
                  backgroundColor: theme.btnBg,
                  color: theme.btnColor,
                  border: 'none',
                }}
                onClick={() => handleActionClick(currentBanner)}
              >
                <span>{currentBanner.action_text}</span>
                {currentBanner.open_in_new_tab ? (
                  <RiExternalLinkLine className="font-size-11" />
                ) : (
                  <RiArrowRightSLine className="font-size-12" />
                )}
              </button>
            )}

            {currentBanner.is_dismissible ? (
              <button
                type="button"
                className="btn btn-sm btn-link text-white p-0 opacity-75 hover-opacity-100 d-inline-flex align-items-center"
                onClick={() => handleDismiss(currentBanner.id)}
                title="Dismiss banner"
                style={{ lineHeight: 1 }}
              >
                <RiCloseLine className="font-size-18" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopNotificationBanner;
